"use client";

import DOMPurify from "dompurify";
import React, { Suspense, useEffect, useState } from "react";

interface FeedItem {
  title: [{ _: string }];
  link: [{ $: { href: string } }];
  content: [{ _: string }];
  published: [string];
  source: [{ title: [string] }];
}

interface AtomFeed {
  feed: {
    entry: FeedItem[];
  };
}

interface SanitizeOptions {
  text: string;
  maxLength: number;
}

function SkeletonLoader(): React.JSX.Element {
  return (
    <div className="skeleton-loader">
      {Array.from({ length: 5 }).map((_, index) => (
        <div className="skeleton-item" key={index}>
          <div className="skeleton-title"></div>
          <div className="skeleton-date"></div>
          <div className="skeleton-description"></div>
        </div>
      ))}
    </div>
  );
}

function FeedContent({ feedItems }: { feedItems: FeedItem[] }): React.JSX.Element {
  const stripHtml = (html: string): string => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
      .replace(/(\w+) (\d+), (\d+)/, "$1 $2, $3");
  };

  const extractDomain = (url: string): string => {
    try {
      const domain = new URL(url).hostname;
      return domain.startsWith("www.") ? domain.slice(4) : domain;
    } catch {
      return url; // Return the original URL if it's invalid
    }
  };

  const sanitizeAndCleanDescription = ({ text, maxLength }: SanitizeOptions): React.ReactNode => {
    const sanitizedHtml = DOMPurify.sanitize(text);
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = sanitizedHtml;
    let cleanText = tempDiv.textContent || tempDiv.innerText || "";

    if (cleanText.length > maxLength) {
      const lastSentenceEnd = cleanText.substring(0, maxLength).lastIndexOf(".");
      if (lastSentenceEnd > 0) {
        cleanText = cleanText.substring(0, lastSentenceEnd + 1);
      } else {
        cleanText = cleanText.substring(0, maxLength) + "...";
      }
    }

    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // Split the text into parts, separating URLs from regular text
    const parts = cleanText.split(urlRegex);

    // Map over the parts, wrapping URLs in anchor tags
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a href={part} key={index} rel="noopener noreferrer" target="_blank">
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <ul className="rss-feed">
      {feedItems.map((item, index) => {
        const domain = extractDomain(item.link[0].$.href);
        const finalDescription = sanitizeAndCleanDescription({
          maxLength: 200,
          text: item.content[0]._,
        });
        return (
          <li key={index}>
            <article className="feed-item" itemType="http://schema.org/Article" itemScope>
              <div className="feed-item-cell">
                <dl className="headline-with-date">
                  <dt>Headline:</dt>
                  <dd className="headline">
                    <a
                      href={item.link[0].$.href}
                      itemProp="url"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <span itemProp="headline">{stripHtml(item.title[0]._)}</span>
                    </a>
                  </dd>
                  <dt>Date Published:</dt>
                  <dd className="date">
                    <time dateTime={item.published[0]} itemProp="datePublished">
                      {formatDate(item.published[0])}
                    </time>
                  </dd>
                </dl>

                <p itemProp="description">{finalDescription}</p>
                <span
                  className="source"
                  itemProp="publisher"
                  itemType="http://schema.org/Organization"
                  itemScope
                >
                  <a
                    href={item.link[0].$.href}
                    itemProp="name"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {domain}
                  </a>
                </span>
              </div>
            </article>
          </li>
        );
      })}
    </ul>
  );
}

export default function NewsFeed(): React.JSX.Element {
  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRssFeed = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch("/api/google-news-rss-feed");
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data: AtomFeed = (await response.json()) as AtomFeed;
        if (!data.feed || !data.feed.entry) {
          throw new Error("Invalid feed data structure");
        }
        setFeedItems(data.feed.entry);
      } catch (error) {
        console.error("Failed to fetch RSS feed:", error);
        setError("Failed to load news feed. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    void fetchRssFeed();
  }, []);

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <Suspense fallback={<SkeletonLoader />}>
      {isLoading ? <SkeletonLoader /> : <FeedContent feedItems={feedItems} />}
    </Suspense>
  );
}
